import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { disablePushSubscription, enablePushSubscription } from './api';
import s from './SettingsHome.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

function vapidBytes(key: string) {
  const encoded = key.replace(/-/g, '+').replace(/_/g, '/');
  const decoded = atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '='));
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

async function appRegistration() {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const script = registration?.active?.scriptURL || registration?.waiting?.scriptURL || registration?.installing?.scriptURL;
  return registration && script && new URL(script).pathname === '/sw.js' && new URL(registration.scope).pathname === '/' ? registration : null;
}

export function useBrowserPush() {
  const [config, setConfig] = useState<{ enabled: boolean; publicKey: string } | null>(null);
  const [status, setStatus] = useState('正在检查…');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const supported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  useEffect(() => {
    let live = true;
    if (!supported) { setStatus('当前浏览器不支持'); return; }
    void Promise.all([api.get<{ enabled: boolean; publicKey: string }>('/api/v2/push/config'), appRegistration()])
      .then(async ([settings, registration]) => {
        if (!live) return;
        setConfig(settings);
        if (!settings.enabled || !settings.publicKey) { setStatus('服务器尚未配置'); return; }
        const subscription = await registration?.pushManager.getSubscription();
        if (!live) return;
        setActive(Boolean(subscription));
        setStatus(subscription ? '已开启' : Notification.permission === 'denied' ? '浏览器已拒绝通知' : '未开启');
      })
      .catch((cause) => { if (live) { setStatus('状态不可用'); setError((cause as Error).message); } });
    return () => { live = false; };
  }, [supported]);

  const toggle = async () => {
    if (!supported || !config?.enabled || !config.publicKey || READONLY || busy) return;
    setBusy(true); setError('');
    try {
      if (active) {
        const registration = await appRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
          await disablePushSubscription(subscription.endpoint);
          await subscription.unsubscribe();
        }
        setActive(false); setStatus('未开启');
      } else {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') { setStatus('浏览器未授予通知权限'); return; }
        const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidBytes(config.publicKey) });
        try { await enablePushSubscription(subscription.toJSON()); }
        catch (cause) { await subscription.unsubscribe(); throw cause; }
        setActive(true); setStatus('已开启');
      }
    } catch (cause) { setError((cause as Error).message || '后台推送操作失败'); }
    finally { setBusy(false); }
  };

  return { status, active, busy, error, toggle, disabled: !supported || !config?.enabled || READONLY || busy };
}

export function PushSetting() {
  const push = useBrowserPush();
  return <div><b>浏览器后台推送</b><small>{push.error || push.status}</small><button type="button" className={s.pushButton} disabled={push.disabled} onClick={() => void push.toggle()}>{push.busy ? '处理中…' : push.active ? '关闭' : '开启'}</button></div>;
}
